import { getApiUrl } from '../config/api-config';

/**
 * What the current session may do, as resolved by the server.
 *
 * Mirrors `CapabilitiesResponse` on the backend. Design:
 * ANONYMOUS_EVALUATION_MODE.md section 3.5.2.
 *
 * ADVISORY, NEVER AUTHORITATIVE. Every endpoint enforces independently, so
 * flipping a flag here grants nothing — it only produces a 403 the ugly way,
 * with the user clicking something that then fails. This shapes the UI; it does
 * not guard anything.
 *
 * Note what is deliberately absent: the tier's numeric allowances. The UI is
 * meant to ask "may I", never to re-derive entitlement from numbers. Both
 * fail-open bugs of 2026-09-01 were UI code deciding permission from tier data.
 */
export interface Capabilities {
  identity: 'ANONYMOUS' | 'LICENSED';
  /** Tier name, or null for a session with no licence. */
  tier: string | null;
  canUploadPdf: boolean;
  uploadsRemaining: number;
  canDrawFields: boolean;
  /** Seeing the flattened result. Not the same as receiving it. */
  canPreviewFlattened: boolean;
  canDownloadFillablePdf: boolean;
  canDownloadFlattenedPdf: boolean;
  canGenerateProject: boolean;
  canUseSessionLibrary: boolean;
  /**
   * ISO-8601 hard ceiling for a guest session, or null when licensed.
   * The ceiling, not the sliding window — it is the one that cannot be
   * extended, so it is the honest thing to show.
   */
  sessionExpiresAt: string | null;
}

/**
 * Resolve the current session's capabilities.
 *
 * `credentials: 'include'` matters more here than elsewhere: the guest cookie
 * is issued by this very call on first contact, and it is HttpOnly, so the
 * browser must be allowed to store and return it. Without it every visit would
 * mint a new guest identity and no one would ever have a second upload.
 */
export async function fetchCapabilities(): Promise<Capabilities | null> {
  try {
    const response = await fetch(getApiUrl('/api/capabilities'), {
      credentials: 'include',
    });

    if (!response.ok) {
      console.error('Failed to fetch capabilities:', response.status);
      return null;
    }

    return (await response.json()) as Capabilities;
  } catch (error) {
    console.error('Error fetching capabilities:', error);
    return null;
  }
}
